<template>
  <div class="w-full h-full">
    <AlertDialog v-if="showFormModal" :open="showFormModal" @update:open="(v) => (showFormModal = v)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Edit timeframe</AlertDialogTitle>
        </AlertDialogHeader>
        <div class="flex flex-col gap-3">
          <label class="text-sm">Start date
            <Input v-model="formStart" type="date" />
          </label>
          <label class="text-sm">End date
            <Input v-model="formEnd" type="date" />
          </label>
          <label class="text-sm">Interval
            <Select v-model="interval">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Interval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="formStart == '' || formEnd == ''"
            @click="() => { updateAnalytics(); showFormModal = false }"
          >Apply</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <div v-if="!finishedloading" class="space-y-4">
      <Skeleton class="h-20 w-full" />
      <Skeleton class="h-96 w-full" />
    </div>

    <!-- Retailer summary in a white card, mirroring the offer page header but with a
         smaller logo since there is less content here. -->
    <Card v-if="finishedloading && retailer" class="mb-4">
      <CardContent class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3 min-w-0">
          <div
            class="size-12 shrink-0 rounded-lg border border-border bg-white shadow-sm flex items-center justify-center overflow-hidden"
          >
            <img
              v-if="retailer.logo"
              class="max-h-full max-w-full object-contain"
              :src="retailer.logo"
              alt=""
            />
            <Store v-else class="size-5 text-muted-foreground" />
          </div>
          <div class="min-w-0">
            <p class="text-lg font-semibold tracking-tight truncate">{{ retailer.retailerName }}</p>
            <p
              v-if="retailer.personalisationCategories && retailer.personalisationCategories.length"
              class="text-sm text-muted-foreground truncate"
            >
              {{ retailer.personalisationCategories[0] }}
            </p>
          </div>
        </div>
        <a
          v-if="formattedRetailerWebsite"
          :href="formattedRetailerWebsite"
          target="_blank"
          class="shrink-0"
        >
          <Button size="sm" variant="outline">
            <ExternalLink class="w-4 h-4 mr-1" /> Website
          </Button>
        </a>
      </CardContent>
    </Card>

    <Card v-if="finishedloading">
      <CardHeader>
        <CardTitle>Available offers</CardTitle>
        <CardDescription>View the available offers for this merchant</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="finalOffersAvalaible"
          :loading="!finishedloading"
          :error="error"
          @retry="() => { getRetailer(); getActivatedOffers(0, 50) }"
        >
          <template #item-retailer="retailers">
            <div class="flex items-center gap-2.5">
              <img v-if="retailers.retailer && retailers.retailer.logo" :src="retailers.retailer.logo" alt="" class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" />
              <span class="text-sm font-medium truncate" :title="offerRetailerName(retailers)">{{ offerRetailerName(retailers) }}</span>
            </div>
          </template>
          <template #item-supplier="retailers">
            <span class="text-sm whitespace-nowrap">{{ retailers.supplier }}</span>
          </template>
          <template #item-currencyList="retailers">
            <div class="flex flex-wrap items-center gap-1">
              <Badge v-for="(c, i) in (retailers.currencyList || [])" :key="i" variant="outline">{{ c }}</Badge>
              <span v-if="!retailers.currencyList || !retailers.currencyList.length" class="text-sm text-muted-foreground">—</span>
            </div>
          </template>
          <template #item-offertype="retailers">
            <Badge variant="secondary">{{ formatOfferType(retailers.offerType) }}</Badge>
          </template>
          <template #item-cashbackAmount="retailers">
            <span class="text-sm tabular-nums whitespace-nowrap">{{ cashbackAmount(retailers) }}</span>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                @click="$router.push(`/retailer/${(retailers.retailer && retailers.retailer.id) || retailerId}/offer/${retailers.id}`)"
              >View</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@boogi/ui'
import { Store, ExternalLink } from 'lucide-vue-next'

import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Input,
    Skeleton,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Store,
    ExternalLink,
  },
  props: {},

  data() {
    return {
      retailerId: this.$route.params.retailerId,
      retailer: undefined,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      loadingActivatedOffers: true,
      loadingOffers: true,
      loadingRetailer: true,

      headersTable1: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Supplier', value: 'supplier' },
        { text: 'Country', value: 'country' },
        { text: 'Currency', value: 'currencyList' },

        { text: 'Offer type', value: 'offertype' },
        { text: 'Total cashback amount', value: 'cashbackAmount' },
        { text: 'Action', value: 'action' },
      ],

      activatedOffersTableHeader: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Supplier', value: 'supplier' },
        { text: 'Country', value: 'offer.country' },
        { text: 'Currency', value: 'offer.currencyList' },
        { text: 'Offer type', value: 'offertype' },
        { text: 'Cashback amount', value: 'cashbackAmount' },
        { text: 'Availability status', value: 'active' },
        { text: 'Approval status', value: 'offerApproval' },
        { text: 'Action', value: 'action' },
      ],
      offerIds: undefined,

      offers: [],
      activatedOffers: [],

      interval: 'week',

      chartOptions: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Transaction count',
          },
        },
      },

      chart2Options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Transaction volume',
          },
        },
      },

      channelTable: [
        { text: 'Channel name', value: 'channelName' },
        { text: 'Address', value: 'address' },
        { text: 'City', value: 'city' },
        { text: 'Postcode', value: 'postcode' },
        { text: 'Channel Type', value: 'channelType' },
        { text: 'Mids', value: 'mids' },
      ],
      startDate: '',
      endDate: '',
      analytics: undefined,
      allTransactionsAnalysis: undefined,
      transactionChartData: undefined,
      transactionVolumeChart: undefined,

      channels: [],
      // showFormModal:false,
      formStart: '',
      formEnd: '',
    }
  },
  created() {
    if (this.$route.query.start && this.$route.query.end) {
      this.formStart = this.$route.query.start
      this.formEnd = this.$route.query.end
      this.startDate = DateTime.fromFormat(this.formStart, 'dd-mm-yyyy').startOf('day')
      this.endDate = DateTime.fromFormat(this.formEnd, 'dd-mm-yyyy').endOf('day')
    } else {
      const now = DateTime.local()
      const startOfMonth = now.startOf('month')
      const endOfMonth = now.endOf('month')

      this.startDate = startOfMonth.minus({ month: 2 })
      this.endDate = endOfMonth
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
    }

    this.getRetailer()
    this.getActivatedOffers(0, 50)
  },
  methods: {
    formatOfferType(type) {
      if (type === 'PAYMENT') return 'Payment-linked'
      if (type === 'ONLINE') return 'Affiliate links'
      if (type === 'CLICK_TO_ACTIVATE') return 'Click to activate'
      return type
    },

    // Offers can come back without a nested retailer (for example when the offer is
    // hidden from the catalogue), so fall back to the retailer this page is showing.
    offerRetailerName(offer) {
      if (offer && offer.retailer && offer.retailer.retailerName) return offer.retailer.retailerName
      if (this.retailer && this.retailer.retailerName) return this.retailer.retailerName
      return '—'
    },

    // Not every offer carries a cashback rule. Reading through the chain blindly threw
    // during render, which blanked the whole page rather than just this cell.
    cashbackAmount(offer) {
      const value =
        offer &&
        offer.cashbackRules &&
        offer.cashbackRules[0] &&
        offer.cashbackRules[0].conditions &&
        offer.cashbackRules[0].conditions[0] &&
        offer.cashbackRules[0].conditions[0].value

      if (!value || value.maxAvailableValue == null) return '—'

      // PERCENTAGE_MAX ("X% up to £Y") is a percentage rate too — only FIXED is a £ amount.
      if (value.type === 'PERCENTAGE' || value.type === 'PERCENTAGE_MAX') {
        const cap = value.maximum > 0 ? ` (up to £${value.maximum})` : ''
        return `${value.maxAvailableValue}%${cap}`
      }

      return `${this.getCurrencySymbol(offer.currencyList)}${value.maxAvailableValue}`
    },

    // Currency code (GBP) → symbol (£) so amounts read as "£2", not "GBP2".
    getCurrencySymbol(currencyList, locale = 'en-US') {
      const code = (currencyList && currencyList[0]) || 'GBP'
      try {
        return (0)
          .toLocaleString(locale, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .replace(/\d/g, '')
          .trim()
      } catch (e) {
        return '£'
      }
    },

    fillMissingDates(data, interval = { weeks: 1 }) {
      const { DateTime } = require('luxon') // Ensure DateTime is imported

      // Convert date strings to DateTime objects for sorting and iteration
      const sortedData = data
        .map((entry) => ({
          ...entry,
          id: DateTime.fromFormat(entry.id, 'EEE MMM dd HH:mm:ss z yyyy').startOf(this.interval), // Parse to DateTime
        }))
        .sort((a, b) => a.id - b.id)

      // Define start and end dates as DateTime objects
      const startDate = this.startDate.startOf(this.interval)
      const endDate = this.endDate.endOf(this.interval)

      const filledData = []
      let currentDate = startDate

      // Fill in missing weekly dates
      while (currentDate <= endDate) {
        // Find if an entry exists for the currentDate week
        const existingEntry = sortedData.find((entry) => entry.id.equals(currentDate))
        if (existingEntry) {
          filledData.push(existingEntry)
        } else {
          filledData.push({
            id: currentDate,
            transactionCount: 0,
            transactionVolume: 0,
          })
        }

        // Increment by the interval (e.g., weekly)
        currentDate = currentDate.plus(interval)
      }

      // Convert DateTime objects back to strings for final output
      return filledData.map((entry) => ({
        ...entry,
        id: entry.id.toFormat('EEE MMM dd HH:mm:ss z yyyy'),
      }))
    },

    formatData(data) {
      var x = {}
      x[this.interval] = 1

      var val = this.fillMissingDates(data, x)

      this.transactionChartData = {
        labels: val.map((data) =>
          DateTime.fromFormat(data.id, 'EEE MMM dd HH:mm:ss z yyyy').toLocaleString(
            DateTime.DATE_SHORT
          )
        ),
        datasets: [
          {
            label: 'Transaction Count',
            backgroundColor: 'rgba(55, 162, 235, 1)',
            borderColor: 'rgba(55, 162, 235, 1))',
            borderRadius: 3,
            data: val.map((data) => data.transactionCount),
          },
        ],
      }
      this.transactionVolumeChart = {
        labels: val.map((data) =>
          DateTime.fromFormat(data.id, 'EEE MMM dd HH:mm:ss z yyyy').toLocaleString(
            DateTime.DATE_SHORT
          )
        ),
        datasets: [
          {
            label: 'Transaction Volume',
            backgroundColor: 'rgba(55, 162, 235, 1)',
            borderColor: 'rgba(55, 162, 235, 1))',
            borderRadius: 3,
            data: val.map((data) => Math.abs(data.transactionVolume)),
          },
        ],
      }
    },

    updateAnalytics() {
      this.startDate = DateTime.fromISO(this.formStart).startOf('day')
      this.endDate = DateTime.fromISO(this.formEnd).endOf('day')
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

      this.getRetailer()
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    async getRetailer() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingRetailer = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/retailer/${this.retailerId}?startDate=${this.formStart}&endDate=${this.formEnd}&interval=${this.interval}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.offerIds = object.offers
          this.retailer = object
          this.channels = object.channels
          this.getOffers(this.offerIds)
        } else {
          this.error = true
          // getOffers only runs on success, so release its flag here or the page
          // sits on skeletons forever instead of surfacing the error.
          this.loadingOffers = false
        }
      } catch (e) {
        this.error = true
        this.loadingOffers = false
      } finally {
        this.loadingRetailer = false
      }
    },

    async getOffers(offerIds) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.loadingOffers = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer-by-ids?offerId=${offerIds}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.offers = object
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingOffers = false
      }
    },

    async getActivatedOffers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.loadingActivatedOffers = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/retailer/${this.retailerId}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.activatedOffers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingActivatedOffers = false
      }
    },
  },
  computed: {
    finishedloading() {
      return (
        this.loadingActivatedOffers == false &&
        this.loadingOffers == false &&
        this.loadingRetailer == false
      )
    },
    finalOffersAvalaible() {
      var x = this.offers

      return x
    },
    formattedRetailerWebsite() {
      if (this.retailer == null || this.retailer.retailerWebsite == null) {
        return null
      }

      // Check if retailerWebsite starts with "https://" or "http://"
      if (
        this.retailer.retailerWebsite.startsWith('https://') ||
        this.retailer.retailerWebsite.startsWith('http://')
      ) {
        return this.retailer.retailerWebsite
      } else {
        // If it doesn't, add "https://" prefix
        return `https://${this.retailer.retailerWebsite}`
      }
    },
  },
  mounted() {
    document.title = 'Dashboard | Retailer'
  },
  watch: {},
}
</script>
